import { useState } from "react";
import { Link } from "react-router-dom";
import { useLocalStorage, getLocalDateKey } from "@/hooks/useLocalStorage";
import { ORGANS } from "@/data/organs";
import { OBSERVE_POINTS, CHANGES_TIME, CHANGES_QUESTIONS, CHANGES_NEXT, PADROES_PAGINA_38 } from "@/data/practices";
import { PageHeader, SafetyNote, SourceTag } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Plus, ArrowRight, ChevronLeft, ChevronRight, BookOpen, Clock, BarChart2, RefreshCw, HelpCircle, Heart, Utensils, Moon, Activity, FileText, Lightbulb, Sparkles } from "lucide-react";

const DAYS = ["SEG", "TER", "QUA", "QUI", "SEX", "SÁB", "DOM"];
// 8 categorias fiéis à página impressa 45 / física 47 do PDF
const WEEKLY_CATS = [
  "Sono",
  "Energia",
  "Digestão",
  "Emoções",
  "Dor ou tensão",
  "Alimentação",
  "Movimento",
  "Observações",
];

// 5 pontos do cartão lateral "O que observar" (página impressa 45 / física 47)
const WEEKLY_OBSERVE_GUIDE = [
  { icon: Clock, title: "Horário", desc: "em que o sinal aparece" },
  { icon: BarChart2, title: "Intensidade", desc: "leve, média ou alta" },
  { icon: RefreshCw, title: "O que mudou", desc: "na rotina" },
  { icon: ArrowRight, title: "O que melhorou", desc: "ou piorou" },
  { icon: HelpCircle, title: "Qual órgão", desc: "consultar no guia" },
];

const emptyEntry = () => ({
  data: getLocalDateKey(),
  sinal: "",
  horario: "",
  diaSemana: "",
  onde: "",
  intensidade: 0,
  emocao: "",
  antes: "",
  alimentacao: "",
  orgao: "",
  pagina: "",
  sono: "",
  movimento: "",
  melhorPiorou: "",
  amanha: "",
});

const getMondayKey = (baseDate = new Date()) => {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diff);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatWeekLabel = (mondayStr) => {
  if (!mondayStr || mondayStr === "legacy") return "Registro legado (sem data definida)";
  const [y, m, d] = mondayStr.split("-").map(Number);
  const mon = new Date(y, m - 1, d);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  const pad = (n) => String(n).padStart(2, "0");
  return `Semana de ${pad(mon.getDate())}/${pad(mon.getMonth() + 1)} a ${pad(sun.getDate())}/${pad(sun.getMonth() + 1)}/${sun.getFullYear()}`;
};

const shiftWeek = (mondayStr, deltaWeeks) => {
  const [y, m, d] = mondayStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + deltaWeeks * 7);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
    <input {...props} className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 sm:py-2 text-sm outline-none focus:ring-2 focus:ring-gold transition-colors" />
  </label>
);

export default function Registros() {
  const [entries, setEntries] = useLocalStorage("cc-diary", []);
  const [draft, setDraft] = useState(emptyEntry());

  // Preservação do registro legado cc-weekly sem inventar data
  const [legacyWeekly, setLegacyWeekly] = useLocalStorage("cc-weekly", {});
  // Histórico por semana identificada pela data da segunda-feira
  const [weeklyHistory, setWeeklyHistory] = useLocalStorage("cc-weekly-history", {});
  const currentWeekKey = getMondayKey();
  const [selectedWeek, setSelectedWeek] = useState(currentWeekKey);

  const hasLegacyData = Object.keys(legacyWeekly || {}).length > 0;

  const currentWeeklyData = selectedWeek === "legacy"
    ? (legacyWeekly || {})
    : (weeklyHistory?.[selectedWeek] || {});

  const setDraftField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const saveEntry = () => {
    if (!draft.sinal.trim()) return;
    setEntries((e) => [{ ...draft, id: Date.now() }, ...e]);
    setDraft(emptyEntry());
  };

  const removeEntry = (id) => setEntries((e) => e.filter((x) => x.id !== id));

  const setWeeklyCell = (cat, day, v) => {
    const key = `${cat}|${day}`;
    if (selectedWeek === "legacy") {
      setLegacyWeekly((w) => ({ ...w, [key]: v }));
    } else {
      setWeeklyHistory((wh) => ({
        ...wh,
        [selectedWeek]: {
          ...(wh?.[selectedWeek] || {}),
          [key]: v,
        },
      }));
    }
  };

  return (
    <div className="space-y-8 sm:space-y-10 pb-8 sm:pb-4">
      <PageHeader
        eyebrow="Observação e registros"
        title="Transforme sinais soltos em percepção"
        subtitle="Um sinal isolado diz pouco; a repetição conta uma história. Observar não é se diagnosticar — é aprender a escutar o corpo com mais consciência. Seus registros ficam salvos apenas neste navegador."
      />

      <Tabs defaultValue="diario" className="w-full">
        <TabsList className="flex w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap h-auto gap-1.5 sm:gap-2 bg-secondary/60 p-1.5 rounded-2xl justify-start sm:justify-center">
          <TabsTrigger value="padroes" data-testid="tab-padroes" className="shrink-0 rounded-full px-3.5 py-2 text-xs sm:text-sm">Como observar padrões</TabsTrigger>
          <TabsTrigger value="diario" data-testid="tab-diario" className="shrink-0 rounded-full px-3.5 py-2 text-xs sm:text-sm">Diário de observação</TabsTrigger>
          <TabsTrigger value="semanal" data-testid="tab-semanal" className="shrink-0 rounded-full px-3.5 py-2 text-xs sm:text-sm">Registro semanal</TabsTrigger>
          <TabsTrigger value="tempo" data-testid="tab-tempo" className="shrink-0 rounded-full px-3.5 py-2 text-xs sm:text-sm">Padrões no tempo</TabsTrigger>
        </TabsList>

        {/* PÁGINA FÍSICA 38 / IMPRESSA 36: COMO OBSERVAR SEUS PADRÕES NA PRÁTICA */}
        <TabsContent value="padroes" className="mt-6 space-y-6 sm:space-y-8" data-testid="section-observar-padroes">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-border/60 pb-4">
            <div>
              <h3 className="font-serif text-2xl sm:text-3xl font-medium text-foreground">
                {PADROES_PAGINA_38.titulo}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {PADROES_PAGINA_38.subtitulo}
              </p>
            </div>
            <SourceTag source={PADROES_PAGINA_38.source} />
          </div>

          {/* Texto explicativo + Destaque */}
          <div className="grid gap-6 lg:grid-cols-12 items-start">
            <div className="lg:col-span-7 space-y-4 text-base text-muted-foreground leading-relaxed">
              <p className="font-serif text-lg text-foreground font-medium">
                {PADROES_PAGINA_38.intro}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-gold/40 bg-gold/10 p-5 space-y-1 text-center">
                <p className="font-serif text-base sm:text-lg font-semibold text-foreground">
                  {PADROES_PAGINA_38.destaque.linha1}
                </p>
                <p className="text-sm font-medium text-gold">
                  {PADROES_PAGINA_38.destaque.linha2}
                </p>
              </div>
            </div>
          </div>

          {/* O que observar VS Como anotar */}
          <div className="grid gap-6 lg:grid-cols-12 items-start">
            {/* O que observar */}
            <div className="lg:col-span-7 rounded-3xl border border-border bg-card p-4 sm:p-6 lg:p-7 space-y-4" data-testid="box-o-que-observar">
              <h4 className="font-serif text-xl font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-gold" />
                {PADROES_PAGINA_38.oQueObservar.titulo}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                {PADROES_PAGINA_38.oQueObservar.itens.map((item, i) => {
                  const ICONS = [Clock, Heart, Utensils, Moon, Activity, BarChart2];
                  const ItemIcon = ICONS[i] || Sparkles;
                  return (
                    <div key={i} className="flex items-center gap-2.5 rounded-2xl bg-secondary/40 border border-border/60 p-3 sm:p-3.5">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-gold">
                        <ItemIcon className="h-4 w-4" />
                      </span>
                      <span className="text-xs sm:text-sm font-medium text-foreground">{item}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Como anotar */}
            <div className="lg:col-span-5 rounded-3xl border border-border bg-card p-4 sm:p-6 lg:p-7 space-y-3" data-testid="box-como-anotar">
              <h4 className="font-serif text-xl font-semibold text-foreground flex items-center gap-2">
                <FileText className="h-5 w-5 text-gold" />
                {PADROES_PAGINA_38.comoAnotar.titulo}
              </h4>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {PADROES_PAGINA_38.comoAnotar.texto}
              </p>
            </div>
          </div>

          {/* Os 6 pontos do padrão */}
          <div className="rounded-3xl border border-border bg-card p-4 sm:p-6 lg:p-8 space-y-5" data-testid="box-6-pontos-padrao">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 border-b border-border/60 pb-3">
              <h4 className="font-serif text-xl font-semibold text-foreground">
                Os 6 pontos do padrão
              </h4>
              <span className="text-xs text-muted-foreground">Roteiro de percepção da MTC</span>
            </div>
            <div className="grid gap-3.5 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {OBSERVE_POINTS.map((p) => (
                <div key={p.n} className="flex items-start gap-3 rounded-2xl bg-secondary/40 border border-border/60 p-3.5 sm:p-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold/20 text-gold text-xs font-semibold mt-0.5">
                    {p.n}
                  </span>
                  <div>
                    <p className="font-serif text-base font-semibold text-foreground">{p.titulo}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 leading-relaxed">{p.texto}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Lembre-se (Rodapé da página 38) */}
          <div className="rounded-2xl border border-gold/30 bg-gold/5 p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3.5 sm:gap-4" data-testid="box-lembre-se-padroes">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gold/20 text-gold">
              <Lightbulb className="h-5 w-5" />
            </span>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-gold block">
                {PADROES_PAGINA_38.lembreSe.titulo}
              </span>
              <p className="text-xs sm:text-sm text-foreground/90 mt-0.5 leading-relaxed">
                {PADROES_PAGINA_38.lembreSe.texto}
              </p>
            </div>
          </div>
        </TabsContent>

        {/* DIÁRIO */}
        <TabsContent value="diario" className="mt-6 space-y-6">
          <div className="rounded-3xl border border-border bg-card p-4 sm:p-6 lg:p-7" data-testid="diary-form">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="font-serif text-xl font-medium">Diário de observação dos sinais</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Registre o que aparece, quando aparece e em qual contexto.</p>
              </div>
              <SourceTag source={{ pdfPage: 46, printedPage: "44" }} />
            </div>
            <div className="mt-4 grid gap-3.5 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Data" type="date" value={draft.data} onChange={(e) => setDraftField("data", e.target.value)} data-testid="diary-data" />
              <Field label="Horário" type="time" value={draft.horario} onChange={(e) => setDraftField("horario", e.target.value)} data-testid="diary-horario" />
              <Field label="Dia da semana" value={draft.diaSemana} onChange={(e) => setDraftField("diaSemana", e.target.value)} data-testid="diary-diasemana" placeholder="Ex: Terça-feira" />
              <label className="block sm:col-span-2 lg:col-span-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Sinal observado</span>
                <input value={draft.sinal} onChange={(e) => setDraftField("sinal", e.target.value)} data-testid="diary-sinal" placeholder="O que o corpo apresentou hoje?" className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 sm:py-2 text-sm outline-none focus:ring-2 focus:ring-gold transition-colors" />
              </label>
              <Field label="Onde senti no corpo?" value={draft.onde} onChange={(e) => setDraftField("onde", e.target.value)} data-testid="diary-onde" placeholder="Ex: Ombro direito, lombar, estômago" />
              <div>
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Intensidade (1 a 5)</span>
                <div className="mt-1.5 flex gap-2 sm:gap-2.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`diary-int-${n}`}
                      onClick={() => setDraftField("intensidade", n)}
                      className={`h-10 w-10 sm:h-9 sm:w-9 rounded-full text-sm font-semibold transition-all active:scale-95 flex items-center justify-center ${
                        draft.intensidade === n
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "bg-secondary text-muted-foreground hover:bg-gold/20"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Emoção presente" value={draft.emocao} onChange={(e) => setDraftField("emocao", e.target.value)} data-testid="diary-emocao" placeholder="Ex: Ansiedade, frustração, calma" />
              <Field label="O que aconteceu antes?" value={draft.antes} onChange={(e) => setDraftField("antes", e.target.value)} placeholder="Contexto ou gatilho" />
              <Field label="Alimentação recente" value={draft.alimentacao} onChange={(e) => setDraftField("alimentacao", e.target.value)} placeholder="O que comeu/bebeu" />
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Órgão possível (consulte no guia)</span>
                <select value={draft.orgao} onChange={(e) => setDraftField("orgao", e.target.value)} data-testid="diary-orgao" className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 sm:py-2 text-sm outline-none focus:ring-2 focus:ring-gold transition-colors">
                  <option value="">— Selecione um órgão —</option>
                  {ORGANS.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
                </select>
              </label>
              <Field label="Página (consulte no guia)" value={draft.pagina} onChange={(e) => setDraftField("pagina", e.target.value)} data-testid="diary-pagina" placeholder="Ex: pág. 12" />
              <Field label="Sono e descanso" value={draft.sono} onChange={(e) => setDraftField("sono", e.target.value)} placeholder="Como dormiu?" />
              <Field label="Movimento do dia" value={draft.movimento} onChange={(e) => setDraftField("movimento", e.target.value)} placeholder="Caminhada, alongamento, repouso" />
              <Field label="O que melhorou ou piorou?" value={draft.melhorPiorou} onChange={(e) => setDraftField("melhorPiorou", e.target.value)} placeholder="Evolução do sinal" />
              <label className="block sm:col-span-2 lg:col-span-3">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">O que posso observar com mais calma amanhã?</span>
                <textarea value={draft.amanha} onChange={(e) => setDraftField("amanha", e.target.value)} rows={2} className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 sm:py-2 text-sm outline-none focus:ring-2 focus:ring-gold transition-colors" placeholder="Ajustes ou percepções para o dia seguinte" />
              </label>
            </div>
            <button
              data-testid="diary-save"
              onClick={saveEntry}
              className="mt-5 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 sm:py-2.5 text-sm font-semibold sm:font-medium text-primary-foreground hover:opacity-90 active:scale-[0.99] transition-all shadow-sm"
            >
              <Plus className="h-4 w-4" /> Salvar registro
            </button>
          </div>

          <div className="space-y-3" data-testid="diary-list">
            {entries.length === 0 && (
              <div
                data-testid="diary-empty"
                className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/60 p-6 sm:p-8 text-center"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold/15 text-gold mb-3">
                  <BookOpen className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <p className="font-serif text-base sm:text-lg font-medium text-foreground">
                  Seu histórico de observações aparecerá aqui
                </p>
                <p className="mt-1 text-xs sm:text-sm text-muted-foreground max-w-sm leading-relaxed">
                  Preencha o formulário acima para criar seu primeiro registro.
                </p>
              </div>
            )}
            {entries.map((en) => (
              <div key={en.id} data-testid={`diary-entry-${en.id}`} className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-foreground">{en.sinal}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {en.data}
                      {en.horario && ` · ${en.horario}`}
                      {en.diaSemana && ` · ${en.diaSemana}`}
                      {en.intensidade > 0 && ` · intensidade ${en.intensidade}/5`}
                    </p>
                  </div>
                  <button
                    data-testid={`diary-remove-${en.id}`}
                    onClick={() => removeEntry(en.id)}
                    className="p-2 -mr-1.5 -mt-1.5 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center justify-center shrink-0 min-h-[36px] min-w-[36px]"
                    title="Excluir"
                    aria-label={`Excluir registro de ${en.sinal || "sinal"}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 text-xs text-muted-foreground border-t border-border/60 pt-3">
                  {en.onde && <div><span className="font-medium text-foreground">Local:</span> {en.onde}</div>}
                  {en.emocao && <div><span className="font-medium text-foreground">Emoção:</span> {en.emocao}</div>}
                  {en.orgao && <div><span className="font-medium text-foreground">Órgão:</span> {en.orgao}</div>}
                  {en.pagina && <div><span className="font-medium text-foreground">Pág. no guia:</span> {en.pagina}</div>}
                  {en.antes && <div><span className="font-medium text-foreground">Antes:</span> {en.antes}</div>}
                  {en.alimentacao && <div><span className="font-medium text-foreground">Alimentação:</span> {en.alimentacao}</div>}
                  {en.sono && <div><span className="font-medium text-foreground">Sono:</span> {en.sono}</div>}
                  {en.movimento && <div><span className="font-medium text-foreground">Movimento:</span> {en.movimento}</div>}
                  {en.melhorPiorou && <div><span className="font-medium text-foreground">Evolução:</span> {en.melhorPiorou}</div>}
                  {en.amanha && <div className="sm:col-span-2 lg:col-span-3"><span className="font-medium text-foreground">Observar amanhã:</span> {en.amanha}</div>}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-border bg-secondary/40 p-4 text-xs text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Como usar este diário:</span> Use este espaço diariamente ou sempre que sentir algo diferente. Quanto mais regular for sua observação, mais claros ficam os padrões.
          </div>

          <SafetyNote>IMPORTANTE: este diário não faz diagnóstico. Use-o como apoio de autoconsciência e procure avaliação profissional quando necessário.</SafetyNote>
        </TabsContent>

        {/* SEMANAL */}
        <TabsContent value="semanal" className="mt-6 space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-serif text-xl font-medium">Registro semanal de padrões</h3>
              <p className="text-sm text-muted-foreground">Observe repetições com calma. O corpo costuma falar em ciclos.</p>
            </div>
            <SourceTag source={{ pdfPage: 47, printedPage: "45" }} />
          </div>

          {/* Navegação entre semanas e preservação de registro legado */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3.5 sm:p-4">
            <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setSelectedWeek((cur) => cur === "legacy" ? currentWeekKey : shiftWeek(cur, -1))}
                className="inline-flex h-9 w-9 sm:h-8 sm:w-8 items-center justify-center rounded-lg border border-input hover:bg-secondary text-foreground shrink-0"
                title="Semana anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setSelectedWeek((cur) => cur === "legacy" ? currentWeekKey : shiftWeek(cur, 1))}
                className="inline-flex h-9 w-9 sm:h-8 sm:w-8 items-center justify-center rounded-lg border border-input hover:bg-secondary text-foreground shrink-0"
                title="Próxima semana"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <span className="text-xs sm:text-sm font-medium text-foreground px-1 truncate">
                {formatWeekLabel(selectedWeek)}
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {selectedWeek !== currentWeekKey && (
                <button
                  type="button"
                  onClick={() => setSelectedWeek(currentWeekKey)}
                  className="rounded-full border border-gold/40 px-3 py-1.5 sm:py-1 text-xs font-medium text-foreground hover:bg-secondary"
                >
                  Semana atual
                </button>
              )}
              {hasLegacyData && selectedWeek !== "legacy" && (
                <button
                  type="button"
                  onClick={() => setSelectedWeek("legacy")}
                  className="rounded-full border border-border bg-secondary/80 px-3 py-1.5 sm:py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                  title="Acessar anotações da versão anterior sem data"
                >
                  Ver registro anterior
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-12">
            {/* Grade dos 7 dias com as 8 categorias oficiais do PDF impresso 45 */}
            <div className="lg:col-span-9">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="weekly-grid">
                {DAYS.map((day) => (
                  <div key={day} className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                    <h4 className="font-serif text-lg font-semibold text-gold">{day}</h4>
                    <div className="mt-3 space-y-2">
                      {WEEKLY_CATS.map((cat) => (
                        <label key={cat} className="block">
                          <span className="text-[11px] text-muted-foreground">{cat}</span>
                          <input
                            value={currentWeeklyData[`${cat}|${day}`] || ""}
                            onChange={(e) => setWeeklyCell(cat, day, e.target.value)}
                            data-testid={`weekly-${day}-${cat}`}
                            className="mt-0.5 w-full rounded-lg border border-input bg-background px-2.5 py-2 sm:py-1.5 text-sm outline-none focus:ring-2 focus:ring-gold transition-colors"
                            placeholder="—"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Guia lateral 'O QUE OBSERVAR' da página 45 do PDF */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl border border-gold/30 bg-card p-4 sm:p-5 space-y-4">
                <div className="border-b border-gold/20 pb-3">
                  <h4 className="font-serif text-base font-semibold text-foreground">O que observar</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">Critérios para registrar repetições</p>
                </div>
                <div className="space-y-3.5">
                  {WEEKLY_OBSERVE_GUIDE.map((g, i) => {
                    const Icon = g.icon;
                    return (
                      <div key={i} className="flex items-start gap-2.5">
                        <Icon className="h-4 w-4 shrink-0 mt-0.5 text-gold" />
                        <div>
                          <p className="text-xs font-medium text-foreground">{g.title}</p>
                          <p className="text-[11px] text-muted-foreground">{g.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-border pt-3">
                  <Link to="/orgaos" className="inline-flex items-center gap-1.5 text-xs font-medium text-gold hover:underline">
                    <BookOpen className="h-3.5 w-3.5" /> Consultar os órgãos
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <SafetyNote>Padrões podem orientar o autocuidado, mas sintomas persistentes, intensos ou preocupantes pedem avaliação profissional.</SafetyNote>
        </TabsContent>

        {/* TEMPO */}
        <TabsContent value="tempo" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-2xl font-medium">Como ler as mudanças no tempo</h3>
            <SourceTag source={{ pdfPage: 48, printedPage: "46" }} />
          </div>
          <div className="grid gap-4 sm:gap-5 sm:grid-cols-3">
            {CHANGES_TIME.map((t, i) => (
              <div key={i} className="rounded-3xl border border-border bg-card p-4 sm:p-6">
                <p className="font-serif text-2xl font-semibold text-gold">{t.periodo}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.texto}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-3xl border border-border bg-card p-4 sm:p-6">
              <h4 className="font-serif text-lg font-medium">Perguntas que ajudam</h4>
              <ul className="mt-3 space-y-2">
                {CHANGES_QUESTIONS.map((q, i) => (
                  <li key={i} className="flex gap-2 text-sm text-foreground">
                    <span className="text-gold font-bold">?</span> {q}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-border bg-card p-4 sm:p-6">
              <h4 className="font-serif text-lg font-medium">Próximo passo</h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {CHANGES_NEXT.map((n, i) => <span key={i} className="rounded-full bg-secondary px-3 py-1.5 text-sm text-foreground">{n}</span>)}
              </div>
              <Link to="/orgaos" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-gold">Consultar os órgãos <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>

          <SafetyNote tone="warn">Evite concluir rápido. A visão da MTC ajuda a refletir sobre conexões, mas não substitui avaliação médica nem exames quando necessários.</SafetyNote>
        </TabsContent>
      </Tabs>
    </div>
  );
}
